<template>
  <div>
    <wc-data-box
      title='Work Required'
      titleIcon='mdi-view-headline'
      titleIconColor='info'
      :loading='isLoading'
      :showAdd='!estimate.is_sold && !fieldcrew'
      @add='edit'
    >
      <v-data-table
        v-if='!$vuetify.breakpoint.smAndDown'
        dense
        :items='options'
        :headers='headers'
        no-data-text="No Outstanding Work found"
        hide-default-footer
        :items-per-page='-1'
        @click:row='edit'
      >
        <template v-slot:item.going_forward='{ item }'>
          <!-- {{ item.going_forward | trueFalse }} -->
          <wc-checkbox :status='item.going_forward'></wc-checkbox>
        </template>
        <template v-slot:item.time_estimate='{ item }'>
          {{ item.time_estimate + ' mins' }}
        </template>
        <template v-slot:item.product_estimate='{ item }'>
          {{ item.product_estimate | decMoney }}
        </template>
        <template v-slot:body.append>
          <tr>
            <td></td>
            <td align='end' class='font-weight-bold'>{{ timeSum }}</td>
            <td align='end' class='font-weight-bold'>{{ productSum }}</td>
            <td></td>
          </tr>
        </template>
      </v-data-table>

      <div v-else>
        <v-card flat v-if='options.length === 0'>
          <v-card-text>No Outstanding Work found</v-card-text>
        </v-card>
        <template v-for='(option, index) in options'>
          <v-divider v-if='index > 0' :key='option.id'></v-divider>
          <v-card flat :key="index + '-' + option.id" @click='edit(option)'>
            <v-card-subtitle class='pb-2'>
              <span class='grey--text text--darken-2'>{{ option.description }}</span>
            </v-card-subtitle>
            <v-card-text class='d-flex align-end'>
              <v-icon :color='product.color'>{{ product.icon }}</v-icon>
              <span class='black--text mr-3'>{{ option.product_estimate | decMoney }}</span>
              <v-icon :color='labour.color'>{{ labour.icon }}</v-icon>
              <span class='black--text mr-3'>{{ option.time_estimate + ' mins'}}</span>
              <v-spacer></v-spacer>
              <span class='black--text ml-1'>Auth?</span>
              <v-icon :color='option.going_forward ? "info" : ""'>
                {{ option.going_forward ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
            </v-card-text>
          </v-card>
        </template>
      </div>

    </wc-data-box>

    <wc-dialog
      width='600'
      :title='optionCopy.id ? "Edit Option" : "Add Option"'
      :showDialog='showEditDialog'
      @cancel='showEditDialog = false'
      @save='save'
      :disableButtons='!isFormValid'
      :loadingButtons='isLoading'
      :fullscreen='$vuetify.breakpoint.xs'
    >
      <template v-slot:dialog-actions v-if='optionCopy.id'>
        <v-btn text @click='showConfirmDelete = true' color='error'>Delete</v-btn>
      </template>

      <v-form v-model='isFormValid' ref='form'>
        <v-row>
          <v-col cols='12'>
            <v-textarea
              outlined
              rows='2'
              auto-grow
              label='Description'
              v-model='optionCopy.description'
              :rules='[rules.required]'
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols='12' md='4'>
            <v-text-field
              id='time'
              label='Time Estimate'
              v-model='optionCopy.time_estimate'
              :rules='[rules.int]'
              @focus='focusTime'
              hint='in mins'
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols='12' md='4'>
            <v-text-field
              id='parts'
              label='Parts Estimate'
              v-model='optionCopy.product_estimate'
              :rules='[rules.decimal]'
              @focus='focusParts'
              reverse
              append-icon='mdi-currency-usd'
              hint='in dollars'
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols='12' md='4'>
            <v-checkbox
              reverse
              label='Going forward?'
              v-model='optionCopy.going_forward'
              hint='(authorized by client?)'
              persistent-hint
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-form>
    </wc-dialog>

    <wc-dialog
      :showDialog='showConfirmDelete'
      title='Are you sure?'
      icon='mdi-alert'
      iconColor='error'
      @cancel='showConfirmDelete = false'
      width='350'
      saveText='Yes, Delete'
      saveColor='error'
      @save='deleteOption'
    >
      <br>
      Are you sure you want to delete this option?<br>
      <br>
      <strong>{{ optionCopy.description }}</strong>
    </wc-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import formRules from '@/mixins/formRules'
import miscServices from '@/services/misc'
import WCCheckbox from '@/components/forms/WCCheckbox'
import estimateOptionsAPI from '@/modules/estimates/api/estimateOptions'

export default {
  name: 'irrigation-service-estimate-options',
  mixins: [formRules],
  props: {
    estimate: {
      required: true
    },
    fieldcrew: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    showEditDialog: false,
    showConfirmDelete: false,
    isFormValid: false,
    isLoading: false,
    optionCopy: {},
    options: [],
    headers: [
      { text: 'Description', value: 'description' },
      { text: 'Time Estimate', value: 'time_estimate', align: 'end', width: '140' },
      { text: 'Parts Estimate', value: 'product_estimate', align: 'end', width: '140' },
      { text: 'Authorized', value: 'going_forward', align: 'end', width: '115' },
    ]
  }),
  mounted() {
    this.getData()
  },
  watch: {
    estimate() {
      if (this.estimate) {
        this.getData()
      }
    }
  },
  computed: {
    ...mapState({
      product: state => state.icons.modules.product,
      labour: state => state.icons.modules.labour,
    }),
    timeSum() {
      let sum = _.sumBy(this.options, 'time_estimate')
      return sum.toString() + ' mins'
    },
    productSum() {
      let sum = _.sumBy(this.options, 'product_estimate')
      return this.$options.filters.decMoney(sum)
    }
  },
  methods: {
    async getData() {
      this.isLoading = true
      this.options = await estimateOptionsAPI.getEstimateOptions(this.estimate.id)
      this.isLoading = false
    },
    edit(option = null) {
      if (this.estimate.is_sold || this.fieldcrew) { return } // no editing allowed
      if (option) {
        this.optionCopy = _.cloneDeep(option)
      } else {
        this.optionCopy = {}
        setTimeout(() => {
          this.$refs.form.resetValidation()
        }, 100)
      }
      this.showEditDialog = true
    },
    cancel () {
      this.showEditDialog = false
    },
    async save () {
      this.isLoading = true
      if (this.optionCopy.id) {
        const tmp = await estimateOptionsAPI.updateEstimateOption(this.optionCopy)
        this.options = miscServices.replaceObject(this.options, tmp)
      } else {
        this.optionCopy.estimate = this.estimate.id
        const tmp = await estimateOptionsAPI.createEstimateOption(this.optionCopy)
        this.options.push(tmp)
      }
      this.showEditDialog = false
      this.isLoading = false
    },
    async deleteOption () {
      this.isLoading = true
      await estimateOptionsAPI.deleteEstimateOption(this.optionCopy)
      this.options = miscServices.removeObject(this.options, this.optionCopy)
      this.showConfirmDelete = false
      this.showEditDialog = false
      this.isLoading = false
    },
    focusTime() {
      setTimeout(() => {
        document.getElementById('time').select()
      }, 50)
    },
    focusParts() {
      setTimeout(() => {
        document.getElementById('parts').select()
      }, 50)
    },
  },
  components: {
    'wc-checkbox': WCCheckbox
  }
}
</script>